import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import {
  ArrowLeft,
  Archive,
  CheckCircle2,
  Copy,
  Download,
  Link2,
  Mail,
  MailOpen,
  MoreHorizontal,
  Pencil,
  Printer,
  Send,
  SendHorizontal,
  Sheet,
  Trash2,
  Undo2
} from 'lucide-react';
import { toast } from 'sonner';
import { Page } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/badge';
import { Card, CardBody, StatTile } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { ErrorState } from '@/components/ui/empty';
import {
  CheckboxField,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  Tooltip
} from '@/components/ui/menu';
import { Select } from '@/components/ui/select';
import { InvoiceDocument, type TemplateId } from './InvoiceDocument';
import { InvoiceEditor } from './InvoiceEditor';
import { InvoiceEmailsCard } from './InvoiceEmailsCard';
import { InvoiceRemindersCard } from './InvoiceRemindersCard';
import { readCreateIntent } from './createIntent';
import { downloadInvoicePdf, printInvoice } from './pdf';
import {
  copyToClipboard,
  useCanTakePayments,
  useBankTransferStatus,
  useCancelBankTransfer,
  useCreatePaymentLink,
  useDeleteInvoice,
  useDiscardInvoice,
  useDownloadExcel,
  useInvoice,
  useMailClientHandoff,
  useSendFollowUp,
  useSendInvoice,
  useSetInvoiceStatus,
  useSetInvoiceTemplate
} from '@/hooks/useInvoices';
import { emailNotConfigured } from '@/api/invoices';
import { useCompanyInfo, useInvoiceSettings, useSaveInvoiceSettings } from '@/hooks/useSettings';
import { useBillingNouns, useSession } from '@/hooks/useSession';
import { formatDate, hoursShort, money, toDayString } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  COMPOSE_APPS,
  openCompose,
  readComposeApp,
  saveComposeApp,
  type ComposeApp
} from '@/lib/mailto';
import type { InvoiceDetail, InvoiceStatus, PaymentMethod } from '@/types';

const METHOD_LABEL: Record<PaymentMethod, string> = {
  card: 'card',
  ach: 'bank transfer'
};

const TEMPLATES: { value: TemplateId; label: string }[] = [
  { value: 'simple', label: 'Simple' },
  { value: 'modern', label: 'Modern' },
  { value: 'professional', label: 'Professional' },
  { value: 'creative', label: 'Creative' }
];

/**
 * The plain-text message the manual path hands to the mail client.
 *
 * ONE SOURCE, and it is the invoice already in the query cache. This is
 * deliberately NOT a client-side copy of the server's HTML template
 * (apps/server/src/lib/invoice-email.ts): two implementations of the same
 * document drift, and every mail client is going to flatten the styling into a
 * compose box anyway. So the body says what the invoice is, what is owed, and
 * where to see it — the document itself travels as the PDF the sender saves out
 * of the print dialog.
 *
 * The subject is character-for-character the one the server uses, so the two
 * send paths read alike in the invoice's email history.
 */
function composeInvoiceEmail(invoice: InvoiceDetail, companyName: string, viewUrl: string | null) {
  const subject = companyName
    ? `Invoice ${invoice.invoiceNumber} from ${companyName}`
    : `Invoice ${invoice.invoiceNumber}`;

  const lines = [
    `Hi ${invoice.billableName ?? 'there'},`,
    '',
    companyName
      ? `Here is invoice ${invoice.invoiceNumber} from ${companyName}.`
      : `Here is invoice ${invoice.invoiceNumber}.`,
    '',
    `Issued: ${formatDate(invoice.issueDate)}`,
    `Due: ${formatDate(invoice.dueDate)}`,
    `Hours: ${hoursShort(invoice.totalHours)}`,
    `Amount due: ${money(invoice.totalAmount)}`
  ];

  if (viewUrl) lines.push('', `View it online: ${viewUrl}`);
  lines.push('', 'Thanks,', companyName || 'Billable');

  return { subject, body: lines.join('\n') };
}

/** One labeled line of an email preview. */
function PreviewRow({
  label,
  value,
  copyText
}: {
  label: string;
  value: ReactNode;
  /** Shows a copy button on the row when set. */
  copyText?: string;
}) {
  return (
    <div className="grid grid-cols-[5rem_minmax(0,1fr)_auto] items-center gap-3 py-1.5">
      <dt className="text-sm text-subtle">{label}</dt>
      <dd className="min-w-0 break-words text-sm">{value}</dd>
      {copyText ? (
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Copy ${label.toLowerCase()}`}
          onClick={() => void copyToClipboard(copyText, label)}
        >
          <Copy />
        </Button>
      ) : (
        <span />
      )}
    </div>
  );
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const nouns = useBillingNouns();
  const session = useSession();

  const invoice = useInvoice(id);
  const company = useCompanyInfo();
  // Only for the manual path: the server puts these on the mail itself, so the
  // handoff has to carry them or the firm's own archive copy goes missing.
  const invoiceSettings = useInvoiceSettings();

  const setStatus = useSetInvoiceStatus();
  const setTemplate = useSetInvoiceTemplate();
  const cancelTransfer = useCancelBankTransfer();
  const transferStatus = useBankTransferStatus(
    id,
    invoice.data?.status === 'sent' && !!invoice.data.paymentSubmittedAt
  );
  const paymentLink = useCreatePaymentLink();
  // Payment links are hidden outright unless this team can actually be paid.
  // A menu item that always errors is worse than no menu item.
  const canTakePayments = useCanTakePayments();
  const followUp = useSendFollowUp();
  const send = useSendInvoice();
  const handoff = useMailClientHandoff();
  const excel = useDownloadExcel();
  const discard = useDiscardInvoice();
  const remove = useDeleteInvoice();

  const [editing, setEditing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [confirm, setConfirm] = useState<'discard' | 'delete' | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  // After a manual send: ask whether to mark the invoice sent. Billable cannot
  // see the user's mail app, so it does not assume the message went out.
  const [askMarkSent, setAskMarkSent] = useState(false);
  const [composeApp, setComposeApp] = useState<ComposeApp>(readComposeApp);
  const copyMode = composeApp === 'copy';
  const [alwaysMarkSent, setAlwaysMarkSent] = useState(false);
  const saveInvoiceSettings = useSaveInvoiceSettings();

  /**
   * "Create and send" on the New invoice screen lands here with an intent in
   * the router state, because sending needs an invoice id that does not exist
   * until the create call returns. See ./createIntent.ts.
   *
   * Two guards, and both are load-bearing:
   *
   *  - the ref, because React runs effects twice in development and this one
   *    sends an email;
   *  - clearing the state with a `replace` navigation, so re-opening this entry
   *    from history — a refresh, a Back — does not send the invoice again.
   *
   * `manual` opens the compose dialog rather than going straight to the mail
   * app. The mailto handoff has to be launched from inside a real click (see
   * lib/mailto.ts: an awaited fetch spends the transient activation and the
   * `_blank` open is then treated as a popup), and by the time we are here the
   * click that started this was two network calls ago.
   */
  const location = useLocation();
  const intent = readCreateIntent(location.state);
  const intentHandled = useRef(false);
  const loadedId = invoice.data?.id;

  useEffect(() => {
    if (!intent || intentHandled.current) return;
    if (!id || loadedId !== id) return;
    intentHandled.current = true;
    navigate(location.pathname, { replace: true, state: null });
    if (intent === 'send') send.mutate(id);
    else setComposeOpen(true);
    // `send` is a stable-enough mutation object and the ref makes re-runs inert.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent, id, loadedId, location.pathname]);

  if (invoice.isError) {
    return (
      <Page title="Invoice">
        <ErrorState
          title="Couldn’t load this invoice"
          message={invoice.error instanceof Error ? invoice.error.message : undefined}
          onRetry={() => void invoice.refetch()}
        />
      </Page>
    );
  }

  const data = invoice.data;
  const today = new Date().toISOString().slice(0, 10);
  const processing = data?.status === 'sent' && !!data.paymentSubmittedAt;
  const transfer = transferStatus.data;
  const debitStarted = transfer?.state === 'debit_started';
  const overdue =
    data?.status === 'sent' && !processing && !!data.dueDate && data.dueDate < today;

  const payUrl = canTakePayments ? (data?.paymentLinkUrl ?? null) : null;
  const copyPayUrl = () => payUrl && void copyToClipboard(payUrl, 'Payment link');

  // ---------------------------------------------------------------- sending
  //
  // GATING. Both paths end with the invoice `sent`, so they are offered on the
  // statuses where that is a true statement of what just happened:
  //
  //  - `draft` — the obvious case, and the only one v1 offered its Email button
  //    on.
  //  - `sent` — resending is routine. A client loses the mail, an address was
  //    wrong, the due date passed. The status does not change, so nothing is
  //    lost by doing it twice.
  //  - `paid` — hidden. The server 400s ("already marked paid"), because
  //    sending would set the status back to `sent` and lose the payment.
  //  - `archived` / `discarded` / `tentative` — hidden. These have been put
  //    away, and mailing one would silently resurrect it as an open invoice.
  //
  // "Send invoice" is additionally admin-only: the route answers 403 "Only
  // administrators can send invoices" to everyone else, and a menu item that
  // always fails is worse than no menu item. The manual path is NOT admin-only
  // — it only logs and moves the status, both of which any member can already
  // do from the Status section of this same menu.
  const sendable = !!data && (data.status === 'draft' || data.status === 'sent');
  const canSendViaBillable = sendable && session.isAdmin;
  // A draft's headline action. Anyone who cannot use the automatic path keeps
  // the old primary, because the manual one needs a dialog and a mail client.
  const showSendPrimary = data?.status === 'draft' && canSendViaBillable;

  const realRecipients = (data?.billTo?.emails ?? []).map((e) => e.trim()).filter(Boolean);
  const realCc = invoiceSettings.data?.ccEmails ?? [];
  const realBcc = invoiceSettings.data?.bccEmails ?? [];

  /**
   * The handoff is the ONE path a real client address can reach a compose
   * window on a developer's machine. `sendEmail` rewrites the recipients of
   * everything Billable sends itself, but it cannot touch a message the user's
   * own mail app delivers — and local development runs against a restored copy
   * of production, where `billTo.emails` is a real person.
   *
   * So the same rule is applied here, from the same source of truth: the server
   * tells us on the session where mail may go, and null — meaning "the real
   * recipients" — is only ever sent by true production.
   */
  const redirectTo = session.emailRedirectTo;
  const recipients = redirectTo ? [redirectTo] : realRecipients;
  const ccEmails = redirectTo ? [] : realCc;
  const bccEmails = redirectTo ? [] : realBcc;
  const companyName = company.data?.name?.trim() ?? '';
  // `paymentLinkUrl`, not `payUrl`: the token URL is Billable's own pay page and
  // it reads fine as a view-only page, so the client still gets a link even
  // where the team cannot take card payments.
  const draft = data ? composeInvoiceEmail(data, companyName, data.paymentLinkUrl) : null;

  // The 501 is a product state, not a failure: no RESEND_API_KEY anywhere in
  // this deployment. The server answers it with everything it would have sent,
  // so that gets shown instead of an error. Dismissing resets the mutation,
  // which clears the error and closes the dialog.
  const notConfigured = emailNotConfigured(send.error);

  const startHandoff = () => {
    if (!data || !draft) return;

    // The open has to happen synchronously in this handler. Awaiting the
    // log-email POST first would spend the user gesture and the browser would
    // treat the new tab as an unsolicited popup — see lib/mailto.ts.
    openCompose(composeApp, {
      to: recipients,
      cc: ccEmails,
      bcc: bccEmails,
      subject: draft.subject,
      body: draft.body
    });
    saveComposeApp(composeApp);
    setComposeOpen(false);

    // Nothing else happens in this tab once the compose window is open. This
    // used to open the print dialog straight after, and with a web mail
    // handler (Gmail in the browser) that pulled focus back here while the
    // compose tab was still loading. The PDF is a button in the dialog now,
    // pressed before this one.
    const autoMark = invoiceSettings.data?.autoMarkSentOnManualSend ?? false;
    const needsStatus = data.status !== 'sent';

    handoff.mutate({
      id: data.id,
      subject: draft.subject,
      body: draft.body,
      to: recipients,
      cc: ccEmails,
      bcc: bccEmails,
      markSent: autoMark && needsStatus
    });

    if (!autoMark && needsStatus) {
      setAlwaysMarkSent(false);
      setAskMarkSent(true);
    }
  };

  const confirmMarkSent = () => {
    if (!data) return;
    setStatus.mutate({ id: data.id, status: 'sent' });
    if (alwaysMarkSent) saveInvoiceSettings.mutate({ autoMarkSentOnManualSend: true });
    setAskMarkSent(false);
  };

  const downloadPdf = async () => {
    if (!data) return;
    setDownloading(true);
    try {
      await downloadInvoicePdf({ invoice: data, company: company.data, clientLabel: nouns.client });
    } catch {
      toast.error('Could not create the PDF.');
    } finally {
      setDownloading(false);
    }
  };

  // Only a Stripe payment writes these; an invoice marked paid by hand has
  // none of them, and then there is nothing honest to say beyond the date.
  const paidCents = data?.amountPaidCents ?? null;
  const paidLine =
    paidCents === null
      ? null
      : `Paid ${money(paidCents / 100)}${
          data?.paymentMethod ? ` by ${METHOD_LABEL[data.paymentMethod]}` : ''
        }`;

  return (
    <Page
      wide
      title={
        data ? (
          <span className="flex items-center gap-3">
            Invoice {data.invoiceNumber}
            <StatusBadge status={data.status} overdue={overdue} processing={processing} />
          </span>
        ) : (
          <span className="skeleton inline-block h-7 w-52 align-middle" />
        )
      }
      description={
        data ? (
          <>
            {data.clientName} · issued {formatDate(data.issueDate)} · due{' '}
            {formatDate(data.dueDate)}
            {data.paidDate ? ` · paid ${formatDate(data.paidDate)}` : ''}
          </>
        ) : (
          <span className="skeleton inline-block h-4 w-72 align-middle" />
        )
      }
      actions={
        <div data-print-hide className="flex flex-col items-end gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="icon" aria-label="Back to invoices" asChild>
            <Link to="/invoices">
              <ArrowLeft />
            </Link>
          </Button>

          <Button variant="secondary" disabled={!data} onClick={() => setEditing(true)}>
            <Pencil />
            Edit
          </Button>

          <Tooltip content="Print or save as PDF">
            <Button variant="secondary" disabled={!data} onClick={printInvoice}>
              <Printer />
              Print
            </Button>
          </Tooltip>

          <Tooltip content="Download as PDF">
            <Button variant="secondary" disabled={!data || downloading} onClick={downloadPdf}>
              <Download />
              Download
            </Button>
          </Tooltip>

          {/*
            The primary action is whatever the invoice is actually waiting for.
            On a draft that is sending it — nobody marks an unsent invoice paid,
            and offering that first invited exactly the wrong click. Once it is
            out, "Mark paid" is the thing left to do by hand.
          */}
          {showSendPrimary ? (
            <Button
              variant="primary"
              data-tour="invoice-send"
              loading={send.isPending}
              onClick={() => data && send.mutate(data.id)}
            >
              <SendHorizontal />
              Send invoice
            </Button>
          ) : data && data.status !== 'paid' && data.status !== 'archived' ? (
            <Button
              variant="primary"
              onClick={() => setStatus.mutate({ id: data.id, status: 'paid' })}
            >
              <CheckCircle2 />
              Mark paid
            </Button>
          ) : null}

          <Menu>
            <MenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="More actions" disabled={!data}>
                <MoreHorizontal />
              </Button>
            </MenuTrigger>
            <MenuContent>
              {/*
                Sending comes first. It is what someone opens this menu to do;
                "Move to sent" is bookkeeping for an invoice that went out some
                other way, and having it above the real send made it the easy
                mis-click — marking an invoice sent that was never sent.
              */}
              {canSendViaBillable || sendable ? (
                <>
                  <MenuLabel>Email</MenuLabel>
                  {canSendViaBillable ? (
                    <MenuItem
                      disabled={send.isPending}
                      onSelect={() => data && send.mutate(data.id)}
                    >
                      <SendHorizontal />
                      {data?.status === 'sent' ? 'Send invoice again' : 'Send invoice'}
                    </MenuItem>
                  ) : null}
                  {sendable ? (
                    <MenuItem onSelect={() => setComposeOpen(true)}>
                      <MailOpen />
                      Manual send…
                    </MenuItem>
                  ) : null}
                  {data?.status === 'sent' ? (
                    <MenuItem onSelect={() => followUp.mutate(data.id)}>
                      <Mail />
                      Send follow-up
                    </MenuItem>
                  ) : null}
                  <MenuSeparator />
                </>
              ) : null}

              <MenuLabel>Status</MenuLabel>
              {(
                ['draft', 'sent', 'paid', 'archived'] as InvoiceStatus[]
              )
                .filter((s) => s !== data?.status)
                .map((status) => (
                  <MenuItem
                    key={status}
                    onSelect={() => data && setStatus.mutate({ id: data.id, status })}
                  >
                    {status === 'draft' ? <Undo2 /> : status === 'sent' ? <Send /> : status === 'paid' ? <CheckCircle2 /> : <Archive />}
                    Move to {status}
                  </MenuItem>
                ))}

              <MenuSeparator />
              <MenuItem
                onSelect={() =>
                  data && excel.mutate({ id: data.id, invoiceNumber: data.invoiceNumber })
                }
              >
                <Sheet />
                Download spreadsheet
              </MenuItem>
              {canTakePayments ? (
                <MenuItem
                  onSelect={() => {
                    if (!data) return;
                    if (payUrl) copyPayUrl();
                    else paymentLink.mutate(data.id);
                  }}
                >
                  <Link2 />
                  {/*
                    Both captions stacked in one grid cell. The widest sets the
                    width, so the row does not resize the moment the link goes
                    from absent to present under the cursor that just made it.
                  */}
                  <span className="grid">
                    <span aria-hidden className="invisible col-start-1 row-start-1">
                      Create payment link
                    </span>
                    <span className="col-start-1 row-start-1">
                      {payUrl ? 'Copy payment link' : 'Create payment link'}
                    </span>
                  </span>
                </MenuItem>
              ) : null}

              {data && (data.status === 'draft' || data.status === 'archived') && session.isAdmin ? (
                <>
                  <MenuSeparator />
                  <MenuItem
                    destructive
                    onSelect={() => setConfirm(data.status === 'draft' ? 'discard' : 'delete')}
                  >
                    <Trash2 />
                    {data.status === 'draft' ? 'Discard draft' : 'Delete invoice'}
                  </MenuItem>
                </>
              ) : null}
            </MenuContent>
          </Menu>
        </div>

        {showSendPrimary ? (
          <p className="max-w-sm text-right text-xs text-muted">
            Sends from <span className="text-[var(--text)]">{session.emailFrom}</span> and marks
            the invoice sent. To send it from your own address instead, use{' '}
            <span className="text-[var(--text)]">Manual send</span>.
          </p>
        ) : null}
        </div>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        {/*
          The preview is plain HTML rendered straight from the query cache, so
          it paints with the rest of the page. No PDF, no iframe, no black box.
        */}
        <div data-print-root data-tour="invoice-preview" className="min-w-0">
          {invoice.isPending ? (
            <InvoiceDocumentSkeleton />
          ) : data ? (
            <InvoiceDocument
              invoice={data}
              company={company.data}
              clientLabel={nouns.client}
            />
          ) : null}
        </div>

        <aside data-print-hide className="space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
            <StatTile
              label="Total due"
              value={money(data?.amountDue)}
              sub={
                data?.lateInterest
                  ? `Includes ${money(data.lateInterest.amount)} late interest`
                  : data
                    ? `${hoursShort(data.totalHours)} hours`
                    : undefined
              }
              loading={invoice.isPending}
              tone={data?.status === 'paid' ? 'positive' : overdue ? 'danger' : 'default'}
            />
            <StatTile
              label="Hourly rate"
              value={money(data?.hourlyRate)}
              sub={data ? `${data.entries.length} line items` : undefined}
              loading={invoice.isPending}
            />
          </div>

          <Card>
            <CardBody className="space-y-4">
              {processing && data?.paymentSubmittedAt ? (
                <div>
                  <p className="mb-1.5 text-sm font-medium">Payment</p>
                  {transfer?.state === 'awaiting_verification' ? (
                    <>
                      <p className="text-sm">Waiting on your client to verify their bank</p>
                      <p className="mt-1 text-sm text-muted">
                        {transfer.depositArrivesOn
                          ? `Stripe sends a small test deposit by ${formatDate(transfer.depositArrivesOn)}. `
                          : ''}
                        The debit starts once they enter its code. Submitted{' '}
                        {formatDate(toDayString(new Date(data.paymentSubmittedAt)))}.
                      </p>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="mt-3"
                        onClick={() =>
                          void copyToClipboard(transfer.verificationUrl, 'Verification link')
                        }
                      >
                        Copy verification link
                      </Button>
                    </>
                  ) : debitStarted ? (
                    <>
                      <p className="text-sm">
                        Bank debit started
                        {transfer.startedOn ? ` ${formatDate(transfer.startedOn)}` : ''}
                      </p>
                      <p className="mt-1 text-sm text-muted">
                        {transfer.expectedOn
                          ? `Funds expected ${formatDate(transfer.expectedOn)}. `
                          : 'Funds usually arrive within a few business days. '}
                        Marked paid when it clears.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm">
                        Bank transfer started{' '}
                        {formatDate(toDayString(new Date(data.paymentSubmittedAt)))}
                      </p>
                      <p className="mt-1 text-sm text-muted">
                        Marked paid when it clears. That takes a few business days, longer if your
                        client’s bank needs confirming first.
                      </p>
                    </>
                  )}
                  {debitStarted ? null : (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="mt-3"
                        loading={cancelTransfer.isPending}
                        onClick={() => cancelTransfer.mutate(data.id)}
                      >
                        Cancel transfer
                      </Button>
                      <p className="mt-1.5 text-sm text-muted">
                        Only possible before the money starts moving. Your client can then pay
                        another way.
                      </p>
                    </>
                  )}
                </div>
              ) : null}

              {paidLine ? (
                <div>
                  <p className="mb-1.5 text-sm font-medium">Payment</p>
                  <p className="tnum text-sm text-emerald-600 dark:text-emerald-400">{paidLine}</p>
                  {data?.platformFeeCents != null ? (
                    <p className="tnum mt-1 text-sm text-muted">
                      Includes {money(data.platformFeeCents / 100)} Billable fee
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div>
                <p className="mb-1.5 text-sm font-medium">Template</p>
                <Select
                  aria-label="Invoice template"
                  className="w-full"
                  value={(data?.templateId as TemplateId) ?? 'simple'}
                  onChange={(v) => data && setTemplate.mutate({ id: data.id, template: v })}
                  options={TEMPLATES}
                  disabled={!data}
                />
                <p className="mt-1.5 text-sm text-muted">
                  Switches instantly — the preview re-renders, nothing regenerates.
                </p>
              </div>

              {payUrl ? (
                <div>
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">Payment link</p>
                    <Button
                      variant="ghost"
                      size="iconSm"
                      aria-label="Copy payment link"
                      onClick={copyPayUrl}
                    >
                      <Copy />
                    </Button>
                  </div>
                  <a
                    href={payUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate text-sm text-[var(--accent)] underline-offset-4 hover:underline"
                  >
                    {payUrl}
                  </a>
                  <p className="mt-1.5 text-sm text-muted">
                    Your client picks card or bank transfer on this page.
                  </p>
                </div>
              ) : null}
            </CardBody>
          </Card>

          {id ? <InvoiceRemindersCard invoiceId={id} status={data?.status} /> : null}
          {id ? <InvoiceEmailsCard invoiceId={id} /> : null}
        </aside>
      </div>

      {data ? (
        <InvoiceEditor open={editing} onOpenChange={setEditing} invoice={data} />
      ) : null}

      {/*
        The manual path, shown before anything happens. It exists because the
        handoff has two moving parts the sender has to be told about up front —
        their own mail client opens, and the invoice is not attached for them.
      */}
      <Dialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        size="lg"
        title="Manual send"
        description="Billable writes the message. Your own mail app sends it, from your address."
        footer={
          <>
            <Button variant="secondary" onClick={() => setComposeOpen(false)}>
              Cancel
            </Button>
            <Button variant="secondary" disabled={!data || downloading} onClick={downloadPdf}>
              <Download />
              Download PDF
            </Button>
            <Button variant="primary" disabled={!draft} onClick={startHandoff}>
              <MailOpen />
              {composeApp === 'copy'
                ? 'Done'
                : composeApp === 'mail_app'
                  ? 'Open my email app'
                  : `Open in ${COMPOSE_APPS.find((a) => a.id === composeApp)?.label}`}
            </Button>
          </>
        }
      >
        {/*
          Never let this dialog imply the client will receive it when they will
          not. The redirect is invisible otherwise — the address in the compose
          window would just quietly be someone else's.
        */}
        {redirectTo ? (
          <div className="mb-4 rounded-xl border border-[var(--line)] bg-[var(--surface-2)] p-3 text-sm">
            <span className="font-medium">This is not going to the client.</span>{' '}
            <span className="text-muted">
              This is a test environment, so everything is addressed to {redirectTo}
              {realRecipients.length ? ` instead of ${realRecipients.join(', ')}` : ''}. Only
              production sends to real recipients.
            </span>
          </div>
        ) : null}

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span className="text-sm text-muted">Write it in</span>
          <div
            role="radiogroup"
            aria-label="Write the email in"
            className="flex items-center gap-1 rounded-lg border border-[var(--line)] p-1"
          >
            {COMPOSE_APPS.map((option) => {
              const active = composeApp === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setComposeApp(option.id)}
                  className={cn(
                    'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                    active
                      ? 'bg-[var(--surface-hover)] text-[var(--text)]'
                      : 'text-muted hover:text-[var(--text)]'
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <dl className="divide-y divide-[var(--line)] border-y border-[var(--line)]">
          <PreviewRow
            label="To"
            value={
              recipients.length ? (
                recipients.join(', ')
              ) : (
                <span className="text-muted">
                  No email address on file — you’ll need to type one in.
                </span>
              )
            }
            copyText={copyMode && recipients.length ? recipients.join(', ') : undefined}
          />
          {ccEmails.length ? (
            <PreviewRow
              label="Cc"
              value={ccEmails.join(', ')}
              copyText={copyMode ? ccEmails.join(', ') : undefined}
            />
          ) : null}
          {bccEmails.length ? (
            <PreviewRow
              label="Bcc"
              value={bccEmails.join(', ')}
              copyText={copyMode ? bccEmails.join(', ') : undefined}
            />
          ) : null}
          <PreviewRow
            label="Subject"
            value={draft?.subject}
            copyText={copyMode ? draft?.subject : undefined}
          />
        </dl>

        <div className="relative mt-4">
          <pre className="scroll-thin max-h-56 overflow-auto whitespace-pre-wrap rounded-xl border border-[var(--line)] bg-[var(--surface-2)] p-3 text-sm">
            {draft?.body}
          </pre>
          {copyMode && draft ? (
            <Button
              variant="secondary"
              size="sm"
              className="absolute right-2 top-2"
              onClick={() => void copyToClipboard(draft.body, 'Message')}
            >
              <Copy />
              Copy message
            </Button>
          ) : null}
        </div>

        <p className="mt-4 text-sm text-muted">
          Attaching the invoice is up to you. Download the PDF first, then attach it in your email
          app.
        </p>
        <p className="mt-2 text-sm text-muted">
          {data?.status === 'sent' || !invoiceSettings.data?.autoMarkSentOnManualSend
            ? 'This is logged on the invoice. Billable can record that it wrote the message, not that you sent it.'
            : 'The invoice is marked sent and logged. Billable can record that it wrote the message, not that you sent it.'}
        </p>

      </Dialog>

      <Dialog
        open={askMarkSent}
        onOpenChange={setAskMarkSent}
        title="Mark as sent?"
        description={
          data
            ? `Once the email is on its way, mark invoice ${data.invoiceNumber} as sent.`
            : undefined
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setAskMarkSent(false)}>
              Not yet
            </Button>
            <Button variant="primary" loading={setStatus.isPending} onClick={confirmMarkSent}>
              Mark as sent
            </Button>
          </>
        }
      >
        <CheckboxField
          checked={alwaysMarkSent}
          onCheckedChange={setAlwaysMarkSent}
          label="Always mark as sent"
          description="Don’t ask after a manual send. You can change this in Invoice settings."
        />
      </Dialog>

      {/*
        No RESEND_API_KEY on this deployment. Nothing was sent, nothing was
        logged and the invoice is untouched — but the server rendered the whole
        message before it found that out and handed it back, so show it.
      */}
      <Dialog
        open={Boolean(notConfigured)}
        onOpenChange={(open) => !open && send.reset()}
        size="lg"
        title="Billable can’t send email yet"
        description="No email provider is configured here, so nothing was sent and the invoice is unchanged. This is the message that was ready to go."
        footer={
          <>
            <Button variant="secondary" onClick={() => send.reset()}>
              Close
            </Button>
            {sendable ? (
              <Button
                variant="primary"
                onClick={() => {
                  send.reset();
                  setComposeOpen(true);
                }}
              >
                <MailOpen />
                Manual send
              </Button>
            ) : null}
          </>
        }
      >
        {notConfigured ? (
          <>
            <dl className="divide-y divide-[var(--line)] border-y border-[var(--line)]">
              <PreviewRow label="From" value={notConfigured.preview.from} />
              <PreviewRow label="To" value={notConfigured.preview.to.join(', ')} />
              {notConfigured.preview.cc.length ? (
                <PreviewRow label="Cc" value={notConfigured.preview.cc.join(', ')} />
              ) : null}
              {notConfigured.preview.bcc.length ? (
                <PreviewRow label="Bcc" value={notConfigured.preview.bcc.join(', ')} />
              ) : null}
              <PreviewRow label="Subject" value={notConfigured.preview.subject} />
              <PreviewRow
                label="Link"
                value={
                  <a
                    href={notConfigured.preview.payUrl ?? notConfigured.preview.viewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="break-all text-[var(--accent)] underline-offset-4 hover:underline"
                  >
                    {notConfigured.preview.payUrl ?? notConfigured.preview.viewUrl}
                  </a>
                }
              />
            </dl>

            {notConfigured.detail ? (
              <p className="mt-4 text-sm text-subtle">{notConfigured.detail}</p>
            ) : null}

          </>
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirm)}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm === 'delete' ? 'Delete this invoice?' : 'Discard this draft?'}
        description={
          confirm === 'delete'
            ? 'The invoice and its line items will be removed. This cannot be undone.'
            : 'The draft is removed and its time entries become billable again.'
        }
        confirmLabel={confirm === 'delete' ? 'Delete' : 'Discard'}
        destructive
        onConfirm={() => {
          if (!data) return;
          if (confirm === 'delete') remove.mutate({ id: data.id });
          else discard.mutate({ id: data.id });
          setConfirm(null);
          navigate('/invoices');
        }}
      />
    </Page>
  );
}

/**
 * Skeleton shaped like the invoice sheet — same paper, same column rhythm — so
 * the layout does not jump when the data arrives.
 */
function InvoiceDocumentSkeleton() {
  return (
    <div
      className="mx-auto w-full max-w-[54rem] bg-white px-10 py-12 shadow-sm ring-1 ring-zinc-200"
      role="status"
      aria-label="Loading invoice"
    >
      <div className="flex items-start justify-between gap-6 border-b-2 border-zinc-200 pb-6">
        <div className="w-1/2 space-y-2">
          <div className="skeleton h-6 w-3/5" />
          <div className="skeleton h-3.5 w-4/5" />
          <div className="skeleton h-3.5 w-2/5" />
        </div>
        <div className="w-2/5 space-y-2">
          <div className="skeleton ml-auto h-5 w-24" />
          <div className="skeleton ml-auto h-3.5 w-32" />
          <div className="skeleton ml-auto h-3.5 w-28" />
        </div>
      </div>
      <div className="grid gap-8 py-6 sm:grid-cols-2">
        {[0, 1].map((i) => (
          <div key={i} className="space-y-2">
            <div className="skeleton h-3 w-16" />
            <div className="skeleton h-4 w-2/3" />
            <div className="skeleton h-3.5 w-4/5" />
          </div>
        ))}
      </div>
      <div className="border-y border-zinc-200 py-2">
        <div className="skeleton h-3 w-full" />
      </div>
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 border-b border-zinc-100 py-3">
          <div className="skeleton h-4 flex-1" />
          <div className="skeleton h-4 w-20" />
          <div className="skeleton h-4 w-12" />
          <div className="skeleton h-4 w-16" />
          <div className="skeleton h-4 w-20" />
        </div>
      ))}
      <div className="mt-6 flex justify-end">
        <div className="w-64 space-y-2">
          <div className="skeleton h-4 w-full" />
          <div className="skeleton h-6 w-full" />
        </div>
      </div>
    </div>
  );
}
